// src/redux/slices/company.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE } from "../../utils/config";
import { apiRequest } from "../../utils/api";
import { readRegistrationFailure, registrationNetworkFailure } from "../../utils/registrationErrors";

const authHeader = () => {
  const token = localStorage.getItem("access");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Public: register company (creates User + CompanyProfile)
 */
export const registerCompany = createAsyncThunk(
  "company/registerCompany",
  async (formValues, thunkAPI) => {
    try {
      const body = {
        company_name: formValues.companyName,
        contact_name: formValues.contactName,
        contact_phone: formValues.contactPhone,
        contact_email: formValues.contactEmail,
        needed_service: formValues.neededService,
        job_type: formValues.job_type,
        email: formValues.email || formValues.contactEmail,
        password: formValues.password,
        email_verification_token: formValues.emailVerificationToken || "",
      };

      const res = await fetch(`${API_BASE}/companies/register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        return thunkAPI.rejectWithValue(await readRegistrationFailure(res));
      }

      return await res.json().catch(() => ({}));
    } catch (e) {
      return thunkAPI.rejectWithValue(e?.status !== undefined ? e : registrationNetworkFailure());
    }
  }
);

/**
 * GET /api/companies/
 */
export const fetchCompanies = createAsyncThunk(
  "company/fetchAll",
  async (_, thunkAPI) => {
    try {
      const res = await apiRequest("/companies/", { headers: { ...authHeader() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Failed to fetch companies");
    }
  }
);

/**
 * POST /api/companies/:id/change_status/
 */
export const changeCompanyStatus = createAsyncThunk(
  "company/changeCompanyStatus",
  async ({ id, status }, thunkAPI) => {
    try {
      const res = await apiRequest(`/companies/${id}/change_status/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      return await res.json();
    } catch (e) {
      return thunkAPI.rejectWithValue(e.message || "Failed to change company status");
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  registering: false,
  registerError: null,
  changing: false,
  changeError: null,
};

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    upsertCompany(state, action) {
      const item = action.payload;
      const i = state.list.findIndex((c) => c.id === item.id);
      if (i >= 0) state.list[i] = item;
      else state.list.unshift(item);
    },
  },
  extraReducers: (b) => {
    // registerCompany
    b.addCase(registerCompany.pending, (s) => {
      s.registering = true;
      s.registerError = null;
    });
    b.addCase(registerCompany.fulfilled, (s) => {
      s.registering = false;
    });
    b.addCase(registerCompany.rejected, (s, a) => {
      s.registering = false;
      s.registerError = a.payload || "Company registration failed";
    });

    // fetchCompanies
    b.addCase(fetchCompanies.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchCompanies.fulfilled, (s, a) => {
      s.loading = false;
      s.list = Array.isArray(a.payload) ? a.payload : [];
    });
    b.addCase(fetchCompanies.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload || "Failed to fetch companies";
    });

    // changeCompanyStatus
    b.addCase(changeCompanyStatus.pending, (s) => {
      s.changing = true;
      s.changeError = null;
    });
    b.addCase(changeCompanyStatus.fulfilled, (s, a) => {
      s.changing = false;
      const updated = a.payload;
      const idx = s.list.findIndex((c) => c.id === updated.id);
      if (idx !== -1) s.list[idx] = updated;
      else s.list.unshift(updated);
    });
    b.addCase(changeCompanyStatus.rejected, (s, a) => {
      s.changing = false;
      s.changeError = a.payload || "Failed to change status";
    });
  },
});

export const { upsertCompany } = companySlice.actions;
export default companySlice.reducer;
